import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, XCircle, MessageCircle } from 'lucide-react';
import TicketCard from './TicketCard';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const MAX_ATTEMPTS = 8;

// Normalize a Malaysian/intl phone to wa.me digits (country code, no +/spaces)
const waDigits = (phone) => {
  let d = (phone || '').replace(/\D/g, '');
  if (d.startsWith('0')) d = '60' + d.slice(1); // local MY number -> +60
  return d;
};

export default function EticketSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('polling'); // polling | paid | failed
  const [tickets, setTickets] = useState([]);
  const [phone, setPhone] = useState('');
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState('failed'); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/api/eticket/payment/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          setTickets(data.tickets || []);
          setPhone(data.holder_phone || '');
          setState('paid');
          return;
        }
        if (data.status === 'expired') { setState('failed'); return; }
      } catch (e) { /* keep trying */ }
      attempts.current += 1;
      if (attempts.current >= MAX_ATTEMPTS) { setState('failed'); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const shareWhatsApp = () => {
    if (!tickets.length) return;
    const origin = window.location.origin;
    const lines = tickets.map((t, i) =>
      `${i + 1}. ${t.rate_name || 'Admission'} — ${origin}/eticket/ticket/${t.qr_token}`
    );
    const msg =
      `🎟️ Your StayMii e-ticket${tickets.length > 1 ? 's' : ''} for ${tickets[0].company_name}:\n\n` +
      `${lines.join('\n')}\n\nPresent the QR at the entrance for admission.`;
    const num = waDigits(phone);
    const base = num ? `https://wa.me/${num}` : `https://wa.me/`;
    window.open(`${base}?text=${encodeURIComponent(msg)}`, '_blank', 'noopener');
  };

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="eticket-success-page">
      {state === 'polling' && (
        <div className="flex flex-col items-center justify-center min-h-[60vh]" data-testid="payment-processing">
          <Loader2 className="w-10 h-10 animate-spin text-[#0C2159]" />
          <p className="mt-4 text-slate-600">Confirming your payment…</p>
        </div>
      )}
      {state === 'paid' && (
        <div>
          <div className="flex flex-col items-center mb-6">
            <CheckCircle2 className="w-12 h-12 text-emerald-500" />
            <h1 className="text-2xl font-bold text-slate-900 mt-2">Payment successful!</h1>
            <p className="text-slate-500 text-sm">
              {tickets.length} e-ticket{tickets.length === 1 ? '' : 's'} ready. Each guest scans their own QR.
            </p>
            <button
              onClick={shareWhatsApp}
              data-testid="share-whatsapp-btn"
              className="mt-4 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold px-5 py-2.5 rounded-xl transition-colors"
            >
              <MessageCircle className="w-5 h-5" /> Send ticket link{tickets.length > 1 ? 's' : ''} to WhatsApp
            </button>
            {phone && <p className="text-xs text-slate-400 mt-2">Opens WhatsApp chat for {phone}</p>}
          </div>
          <div className="space-y-8" data-testid="success-tickets">
            {tickets.map((t) => (
              <div key={t.qr_token}>
                <TicketCard ticket={t} />
                <p className="text-center mt-3 text-sm">
                  <Link className="text-[#0C2159] underline" to={`/eticket/ticket/${t.qr_token}`} data-testid={`view-ticket-link-${t.ticket_number}`}>Open ticket page</Link>
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
      {state === 'failed' && (
        <div className="flex flex-col items-center justify-center min-h-[60vh]" data-testid="payment-failed">
          <XCircle className="w-12 h-12 text-red-500" />
          <p className="mt-4 text-slate-700 font-medium">We couldn't confirm your payment.</p>
          <p className="text-slate-500 text-sm">If you were charged, please check your email or contact support.</p>
        </div>
      )}
    </div>
  );
}
